import { useRef, useState } from "react";
import { SavedView, UpdateSavedViewSaveModel } from "./SavedViewsMenu.types";
import { SavedViewsMenuProps } from "./SavedViewsMenu.tsx";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { FilterPage } from "../../pageView/PageView.types.ts";
import { put } from "../../api/apiShared.ts";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { Result } from "../../api/result.ts";
import { ContractColumn, getFilterIdByEnum } from "../../../contract/contractDisplayFields/ContractDisplayFields.types.ts";
import { getFilterIdByEnum as getSystemFilterIdByEnum, SystemColumn } from "../../../system/systems/systemDisplayFields/SystemDisplayFields.types.ts";
import { useDotLegalMutation, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import useContractDisplayFields from "../../../contract/contractDisplayFields/ContractDisplayFields.hooks.ts";
import useSystemDisplayFields from "../../../system/systems/systemDisplayFields/SystemDisplayFields.hooks.ts";
import { getLegalEntityFilterIdByColumn, LegalEntityColumn } from "../../../legalEntity/legalEntities/legalEntityColumns.ts";
import { getIncidentLogFilterIdByColumn, IncidentLogColumn } from "../../../incidentLog/incidentLogs/incidentLogColumns.ts";
import { getProcessFilterIdByColumn, ProcessColumn } from "../../../process/processes/processColumns.ts";
import { getProcessingActivityFilterIdByColumn, ProcessingActivityColumn } from "../../../processingActivity/processingActivities/processingActivityColumns.ts";
import { getSharingFilterIdByColumn, SharingColumn } from "../../../sharings/sharingColumns.ts";
import { isNullOrWhitespace } from "@common/stringOperations";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";

export function buildSavedViewScopeQuery(scopeId?: string, secondaryScopeId?: string) {
    return (scopeId ? `&scopeId=${scopeId}` : "") + (secondaryScopeId ? `&secondaryScopeId=${secondaryScopeId}` : "");
}

const sortFields = ["sortByColumn", "sortDirection", "sortByCustomFieldId"];

const menuViewportMargin = 16;

// The shared change detection deliberately ignores sort, so re-sorting never marks a view as modified or offers
// "clear filters". A re-sort should still be saveable on its own, so the save button gets this separate check.
export function sortDiffersFromSavedView(currentFilterData: any, savedFilterData: any): boolean {
    return sortFields.some((field) => currentFilterData?.[field] !== savedFilterData?.[field]);
}

export function orderSavedViewsForMenu(views: Array<SavedView>, language: string) {
    // The view that opens for the user is the only one lifted out of the alphabetical list. The backend puts
    // isUserDefault on the organisation default, or on the .legal default view, for a user who has picked none,
    // so those keep the top row until the user chooses a view of their own.
    //
    // The chosen language decides the collation, not the browser locale: in Danish æ, ø and å sort after z,
    // so a view named "Åbne sager" belongs last rather than next to the a's.
    return [
        ...views.filter((view) => view.isUserDefault),
        ...views.filter((view) => !view.isUserDefault).sort((a, b) => a.name.localeCompare(b.name, language)),
    ];
}

// A view without an owner is visible to the whole organisation: global views, the organisation default and the
// .legal provided views alike. Each section keeps the menu ordering, so the view that opens still leads its section.
export function groupSavedViewsForMenu(views: Array<SavedView>, language: string) {
    return {
        sharedViews: orderSavedViewsForMenu(
            views.filter((view) => isNullOrWhitespace(view.userId)),
            language
        ),
        personalViews: orderSavedViewsForMenu(
            views.filter((view) => !isNullOrWhitespace(view.userId)),
            language
        ),
    };
}

export function useSavedViewQuery(page: FilterPage, scopeId?: string, secondaryScopeId?: string) {
    const url = `/saved-views?savedViewType=${page}${buildSavedViewScopeQuery(scopeId, secondaryScopeId)}`;

    const isContractPage = page === FilterPage.Contract;
    const isSystemPage = page === FilterPage.System;

    const { isLoading, refetch, data } = useDotLegalQuery<Array<SavedView>>(url);
    const { isLoading: isContractFieldsLoading, filterIdToEnumMap: contractFilterIdToEnumMap } = useContractDisplayFields(isContractPage);
    const { isLoading: isSystemFieldsLoading, filterIdToEnumMap: systemFilterIdToEnumMap } = useSystemDisplayFields(isSystemPage);
    let views;

    if (data && (!isContractPage || !isContractFieldsLoading) && (!isSystemPage || !isSystemFieldsLoading)) {
        views = mapToColumnIds(page, data, contractFilterIdToEnumMap, systemFilterIdToEnumMap);
    }

    return { isLoading: (isContractPage && isContractFieldsLoading) || (isSystemPage && isSystemFieldsLoading) || isLoading, refetch, data: views };
}

function mapToColumnIds(
    page: FilterPage,
    data: Array<SavedView>,
    contractFilterIdToEnumMap: Record<string, ContractColumn | undefined>,
    systemFilterIdToEnumMap: Record<string, SystemColumn | undefined>
) {
    switch (page) {
        case FilterPage.Contract: {
            let standard = data.find((x) => x.isSystemDefault);
            if (standard && !isNaN(standard.displayData[0])) {
                standard.displayData = standard?.displayData.map((x: ContractColumn) => getFilterIdByEnum(x, contractFilterIdToEnumMap));
            }

            let viewAllContractsWithoutAuth = data.find((x) => x.isFetchAllContractWithoutAuthView);
            if (viewAllContractsWithoutAuth && !isNaN(viewAllContractsWithoutAuth.displayData[0])) {
                viewAllContractsWithoutAuth.displayData = viewAllContractsWithoutAuth?.displayData.map((x: ContractColumn) =>
                    getFilterIdByEnum(x, contractFilterIdToEnumMap)
                );
            }
            break;
        }
        case FilterPage.System: {
            let standard = data.find((x) => x.isSystemDefault);
            if (standard && !isNaN(standard.displayData[0])) {
                standard.displayData = standard?.displayData.map((x: SystemColumn) => getSystemFilterIdByEnum(x, systemFilterIdToEnumMap));
            }
            break;
        }
        // The default view and the four views the Lists button used to offer are all served in enum form; a
        // user-saved view already holds column ids.
        case FilterPage.LegalEntityExternal:
        case FilterPage.LegalEntityGroupCompanies: {
            data.filter((x) => (x.isSystemDefault || x.isSystemProvidedView) && !isNaN(x.displayData[0])).forEach((view) => {
                view.displayData = view.displayData.map((x: LegalEntityColumn) => getLegalEntityFilterIdByColumn(x));
            });
            break;
        }

        case FilterPage.IncidentLog: {
            data.filter((x) => x.isSystemDefault && !isNaN(x.displayData[0])).forEach((view) => {
                view.displayData = view.displayData.map((x: IncidentLogColumn) => getIncidentLogFilterIdByColumn(x));
            });
            break;
        }

        case FilterPage.Process: {
            data.filter((x) => x.isSystemDefault && !isNaN(x.displayData[0])).forEach((view) => {
                view.displayData = view.displayData.map((x: ProcessColumn) => getProcessFilterIdByColumn(x));
            });
            break;
        }

        // The default view and the three standard lists are served in enum form, like on the legal entity pages.
        case FilterPage.ProcessingActivity: {
            data.filter((x) => (x.isSystemDefault || x.isSystemProvidedView) && !isNaN(x.displayData[0])).forEach((view) => {
                view.displayData = view.displayData.map((x: ProcessingActivityColumn) => getProcessingActivityFilterIdByColumn(x));
            });
            break;
        }

        // The default view and the three standard Transfers lists are served in enum form.
        case FilterPage.Sharing: {
            data.filter((x) => (x.isSystemDefault || x.isSystemProvidedView) && !isNaN(x.displayData[0])).forEach((view) => {
                view.displayData = view.displayData.map((x: SharingColumn) => getSharingFilterIdByColumn(x));
            });
            break;
        }
    }

    return data;
}

export function useSavedViewsMenu(props: SavedViewsMenuProps) {
    const snackbar = useDotLegalSnackbar();

    const { translateString, getChosenLanguage } = useTranslation();
    const { permissions } = useUserContext();
    const { isLoading, data, refetch } = useSavedViewQuery(props.filterPage, props.scopeId, props.secondaryScopeId);

    const savedViewsAnchorRef = useRef<HTMLDivElement>(null);
    const [menuMaxHeight, setMenuMaxHeight] = useState<number | undefined>(undefined);
    const [menuOpen, setMenuOpen] = useState(false);
    const [viewToDelete, setViewToDelete] = useState<SavedView | undefined>(undefined);
    const [showCreateViewDialog, setShowCreateViewDialog] = useState(false);
    const [viewToUpdate, setViewToUpdate] = useState<string | undefined>(undefined);
    const [showSetUserDefaultView, setShowSetUserDefaultView] = useState<SavedView | undefined>(undefined);
    const [showSetCompanyDefaultView, setShowSetCompanyDefaultView] = useState<SavedView | undefined>(undefined);
    const [showSetAsGlobalDialog, setShowSetAsGlobalDialog] = useState<SavedView | undefined>(undefined);

    // A menu taller than the space under the button makes MUI slide the whole paper up until its bottom fits,
    // which is how a long view list ends up covering the screen instead of hanging off the button. Capping the
    // paper at the room below keeps it anchored downwards and lets the list scroll inside it instead.
    const openMenu = () => {
        const anchorBottom = savedViewsAnchorRef.current?.getBoundingClientRect().bottom ?? 0;
        setMenuMaxHeight(window.innerHeight - anchorBottom - menuViewportMargin);
        setMenuOpen(true);
    };

    const onViewUpdated = () => {
        setViewToUpdate(undefined);
        refetch();
    };

    const onViewCreated = (viewId: string) => {
        setShowCreateViewDialog(false);
        refetch();
        props.onViewSaved?.(viewId);
    };

    const onToggledDefaultView = () => {
        setShowSetUserDefaultView(undefined);
        refetch();
    };

    const onToggleCustomerDefaultView = () => {
        setShowSetCompanyDefaultView(undefined);
        refetch();
    };

    const onViewSetToGlobal = () => {
        setShowSetAsGlobalDialog(undefined);
        refetch();
    };

    const onViewDeleted = () => {
        setViewToDelete(undefined);
        refetch();
        props.onViewDeleted?.(viewToDelete!.id);
    };

    const saveToCurrentViewMutation = useDotLegalMutation(saveToCurrentViewApi);
    const onSaveDataToCurrentView = (viewId: string) => {
        saveToCurrentViewMutation.mutateAsync(
            { id: viewId, data: { displayConfiguration: props.currentDisplayData, filterConfiguration: props.currentFilterData } },
            {
                onSuccess: (response: Result<string>) => {
                    snackbar.show(translateString("viewUpdated"));
                    refetch();
                },
            }
        );
    };

    const hasOrgDefault = (data ?? []).some((x) => x.isCustomerDefault);
    const visibleViews = hasOrgDefault ? (data ?? []).filter((x) => !x.isSystemDefault) : (data ?? []);
    const savedViews = orderSavedViewsForMenu(visibleViews, getChosenLanguage());
    const { sharedViews, personalViews } = groupSavedViewsForMenu(visibleViews, getChosenLanguage());

    const canManageViews = props.canManageViews ?? true;
    const currentView = savedViews.find((x) => x.id === props.currentViewId);
    // A picker has nothing to save the changes to, so it never shows them
    const hasChangesFromCurrentView =
        canManageViews &&
        currentView !== undefined &&
        (props.hasFilterOrDisplayDataChanged || sortDiffersFromSavedView(props.currentFilterData, currentView.filterData));
    const canSaveToCurrentView =
        hasChangesFromCurrentView &&
        !currentView.isSystemProvidedView &&
        !currentView.isSystemDefault &&
        (permissions.contractManagementPermissions.canManageGlobalViews || (!currentView.isGlobal && !currentView.isCustomerDefault));

    return {
        isLoading,
        savedViews,
        sharedViews,
        personalViews,
        currentView,
        canManageViews,
        hasChangesFromCurrentView,
        canSaveToCurrentView,
        savedViewsAnchorRef,
        menuMaxHeight,
        openMenu,
        viewToDelete,
        setViewToDelete,
        showCreateViewDialog,
        setShowCreateViewDialog,
        viewToUpdate,
        setViewToUpdate,
        showSetUserDefaultView,
        setShowSetUserDefaultView,
        menuOpen,
        setMenuOpen,
        onViewCreated,
        onViewDeleted,
        onViewUpdated,
        onToggledDefaultView,
        onSaveDataToCurrentView,
        showSetCompanyDefaultView,
        setShowSetCompanyDefaultView,
        onToggleCustomerDefaultView,
        showSetAsGlobalDialog,
        setShowSetAsGlobalDialog,
        onViewSetToGlobal,
        isSavingToCurrentView: saveToCurrentViewMutation.isLoading,
    };

    async function saveToCurrentViewApi({ id, data }: { id: string; data: UpdateSavedViewSaveModel }) {
        return await put<string>(`/saved-views/${id}/configuration-data`, data);
    }
}
